import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useApi } from '@backstage/core-plugin-api';
import type {
  Nobl9MetadataDocument,
  Nobl9Project,
  Nobl9Service,
  Nobl9DataSource,
} from './types';

export type Nobl9MetadataState =
  | 'loading'
  | 'ready'
  | 'error'
  | 'refreshing'
  | 'stale';

export interface Nobl9MetadataContextValue {
  state: Nobl9MetadataState;
  error?: Error;
  projects: Nobl9Project[];
  services: Nobl9Service[];
  dataSources: Nobl9DataSource[];
  lastLoaded: string | null;
  sourceUsed?: string;
  fallbackReason?: string;
  ttlMillis: number; // client-side TTL (derived from server ttlSeconds or default)
  isStale: boolean;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
  getServicesForProject: (projectId: string) => Nobl9Service[];
}

const Nobl9MetadataContext = React.createContext<
  Nobl9MetadataContextValue | undefined
>(undefined);

export interface Nobl9MetadataProviderProps {
  children: React.ReactNode;
  defaultTtlMillis?: number;
  pluginId?: string;
}

function computeIsStale(
  doc: Nobl9MetadataDocument | undefined,
  ttlMillis: number,
): boolean {
  if (!doc) return false;
  const loadedAt = Date.parse(doc.generatedAt);
  if (!loadedAt) return false;
  return Date.now() - loadedAt > ttlMillis;
}

export const Nobl9MetadataProvider = ({
  children,
  defaultTtlMillis = 5 * 60 * 1000,
  pluginId = 'nobl9',
}: Nobl9MetadataProviderProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<Nobl9MetadataState>('loading');
  const [error, setError] = useState<Error | undefined>();
  const [doc, setDoc] = useState<Nobl9MetadataDocument | undefined>();
  const inFlight = useRef<Promise<void> | null>(null);
  const refreshRequestedAt = useRef<number>(0);

  const ttlMillis = doc?.ttlSeconds ? doc.ttlSeconds * 1000 : defaultTtlMillis;
  const isStale = computeIsStale(doc, ttlMillis);

  const fetchDoc = useCallback(
    async (force: boolean) => {
      setState(prev => (prev === 'loading' ? 'loading' : 'refreshing'));
      setError(undefined);
      try {
        const baseUrl = await discoveryApi.getBaseUrl(pluginId);
        const url = new URL(baseUrl.replace(/\/$/, '') + '/metadata/all');
        if (force) url.searchParams.set('refresh', '1');
        let resp = await fetchApi.fetch(url.toString());

        if (resp.status === 404 && pluginId === 'nobl9') {
          const fallbackUrl = new URL(
            window.location.origin +
              '/api/nobl9/metadata/all' +
              (force ? '?refresh=1' : ''),
          );
          resp = await fetchApi.fetch(fallbackUrl.toString());
        }
        if (!resp.ok) {
          throw new Error(
            `Failed to load Nobl9 metadata: ${resp.status} ${resp.statusText}`,
          );
        }
        const raw = (await resp.json()) as Nobl9MetadataDocument &
          Record<string, any>;

        // Strict normalization to current schema only.
        // Projects require: appId, projectName, businessUnit
        const normalizedProjects: Nobl9Project[] = Array.isArray(raw.projects)
          ? raw.projects.map(p => ({
              ...p,
              name: (p as any).projectName ?? (p as any).name ?? '',
              appInventoryId:
                (p as any).appId ?? (p as any).appInventoryId ?? undefined,
              bu: (p as any).businessUnit ?? (p as any).bu ?? 'Other',
              displayName:
                (p as any).displayName ??
                (p as any).projectName ??
                (p as any).name ??
                '',
              nfr:
                (p as any).nfr === true ||
                String((p as any).nfr).toLowerCase() === 'true'
                  ? true
                  : undefined,
              pfgOwner:
                (p as any).pfgOwner !== undefined
                  ? String((p as any).pfgOwner)
                  : undefined,
            }))
          : [];
        // Services require: serviceName, projectRef
        const normalizedServices: Nobl9Service[] = Array.isArray(raw.services)
          ? raw.services.map(s => ({
              ...s,
              name: (s as any).serviceName ?? (s as any).name ?? '',
              projectId: (s as any).projectRef ?? (s as any).projectId ?? '',
              displayName:
                (s as any).displayName ??
                (s as any).serviceName ??
                (s as any).name ??
                '',
            }))
          : [];
        // DataSources require: dataSourceName, projectRef
        const normalizedDataSources: Nobl9DataSource[] = Array.isArray(
          raw.dataSources,
        )
          ? raw.dataSources.map(d => ({
              ...d,
              name: (d as any).dataSourceName ?? (d as any).name ?? '',
              projectId: (d as any).projectRef ?? (d as any).projectId ?? '',
              displayName:
                (d as any).displayName ??
                (d as any).dataSourceName ??
                (d as any).name ??
                '',
            }))
          : [];
        const json: Nobl9MetadataDocument = {
          generatedAt: raw.generatedAt,
          projects: normalizedProjects,
          services: normalizedServices.filter(s => !!s.projectId), // drop invalid entries lacking project linkage
          dataSources: normalizedDataSources,
          sourceUsed: raw.sourceUsed,
          fallbackReason: raw.fallbackReason,
          ttlSeconds: (raw as any).ttlSeconds,
        };
        setDoc(json);
        const staleNow = computeIsStale(json, ttlMillis);
        setState(staleNow ? 'stale' : 'ready');
      } catch (e: any) {
        setError(e instanceof Error ? e : new Error(String(e)));
        setState(doc ? 'ready' : 'error'); // retain previous data if present
      }
    },
    [discoveryApi, fetchApi, pluginId, ttlMillis, doc],
  );

  const refresh = useCallback(
    async (opts?: { force?: boolean }) => {
      const force = opts?.force ?? false;
      refreshRequestedAt.current = Date.now();
      if (inFlight.current) {
        return inFlight.current; // avoid duplicate concurrent requests
      }
      const p = fetchDoc(force);
      inFlight.current = p.then(() => {
        inFlight.current = null;
      });
      return inFlight.current;
    },
    [fetchDoc],
  );

  // Initial load
  useEffect(() => {
    refresh({ force: true }).catch(() => {
      /* error handled in fetchDoc */
    });
  }, []);

  // Auto refresh when stale (lazy strategy: trigger only upon becoming stale & user next interactions could rely on it)
  useEffect(() => {
    if (isStale && state === 'ready') {
      // Fire a background refresh without forcing TTL bypass (force false ensures server decides)
      refresh({ force: false }).catch(() => {
        /* ignore */
      });
    }
  }, [isStale, state, refresh]);

  const value: Nobl9MetadataContextValue = {
    state: isStale && state === 'ready' ? 'stale' : state,
    error,
    projects: doc?.projects ?? [],
    services: doc?.services ?? [],
    dataSources: doc?.dataSources ?? [],
    lastLoaded: doc?.generatedAt ?? null,
    sourceUsed: doc?.sourceUsed,
    fallbackReason: doc?.fallbackReason,
    ttlMillis,
    isStale,
    refresh,
    getServicesForProject: (projectId: string) =>
      (doc?.services || []).filter(s => s.projectId === projectId),
  };

  return (
    <Nobl9MetadataContext.Provider value={value}>
      {children}
    </Nobl9MetadataContext.Provider>
  );
};

export function useNobl9Metadata(): Nobl9MetadataContextValue {
  const ctx = useContext(Nobl9MetadataContext);
  if (!ctx) {
    throw new Error(
      'useNobl9Metadata must be used within Nobl9MetadataProvider',
    );
  }
  return ctx;
}
